const isValidCoordinate = (value) => {
  const number = Number(value);
  return Number.isFinite(number);
};

export const DEFAULT_LOCATION_LABEL = "Current Location";

const coerceCoordinate = (value) => {
  if (value === null || value === undefined || value === "") {
    return null;
  }

  const number = Number(value);
  return Number.isFinite(number) ? number : null;
};

const isCoordinateString = (text) =>
  /^(-?\d{1,3}(?:\.\d+)?)\s*,\s*(-?\d{1,3}(?:\.\d+)?)$/.test(String(text || "").trim());

const isLocationPayloadText = (text) => {
  const raw = String(text || "").trim();
  if (!raw) {
    return true;
  }
  if (isCoordinateString(raw)) {
    return true;
  }
  if (!raw.startsWith("{")) {
    return false;
  }
  try {
    const decoded = JSON.parse(raw);
    return isValidCoordinate(decoded?.latitude ?? decoded?.lat)
      && isValidCoordinate(decoded?.longitude ?? decoded?.lng);
  } catch {
    return false;
  }
};

const resolveLocationLabel = (name, address, fallbackText = "") => {
  const label = [name, address].filter(Boolean).join(" - ");
  if (label) {
    return label;
  }
  if (isLocationPayloadText(fallbackText)) {
    return DEFAULT_LOCATION_LABEL;
  }
  const trimmedFallback = String(fallbackText || "").trim();
  return trimmedFallback || DEFAULT_LOCATION_LABEL;
};

const isGoogleMapsUrl = (text) => {
  const raw = String(text || "").trim();
  return Boolean(
    raw
    && /google\.com\/maps|maps\.google|goo\.gl\/maps|maps\.app\.goo/i.test(raw)
  );
};

const parseCoordsFromGoogleMapsUrl = (text) => {
  const raw = String(text || "").trim();
  if (!raw || !isGoogleMapsUrl(raw)) {
    return null;
  }

  const patterns = [
    /[?&]q=(-?\d+(?:\.\d+)?),\s*(-?\d+(?:\.\d+)?)/,
    /[?&]query=(-?\d+(?:\.\d+)?),\s*(-?\d+(?:\.\d+)?)/,
    /@(-?\d+(?:\.\d+)?),\s*(-?\d+(?:\.\d+)?)/,
    /!3d(-?\d+(?:\.\d+)?)!4d(-?\d+(?:\.\d+)?)/,
  ];

  for (const pattern of patterns) {
    const match = raw.match(pattern);
    if (match) {
      return {
        latitude: Number(match[1]),
        longitude: Number(match[2]),
      };
    }
  }

  return null;
};

const extractNameAddressFromData = (data = {}) => ({
  name: String(data?.name || data?.location_name || data?.loc_name || "").trim(),
  address: String(
    data?.address || data?.location_address || data?.loc_address || ""
  ).trim(),
});

const buildLocationFromData = (data, fallbackText = "") => {
  const latitude = coerceCoordinate(data?.latitude ?? data?.lat);
  const longitude = coerceCoordinate(data?.longitude ?? data?.lng);
  const { name, address } = extractNameAddressFromData(data);

  if (latitude === null || longitude === null) {
    if (!name && !address) {
      return null;
    }

    return {
      latitude: null,
      longitude: null,
      label: resolveLocationLabel(name, address, fallbackText),
      name,
      address,
    };
  }

  return {
    latitude,
    longitude,
    label: resolveLocationLabel(name, address, fallbackText),
    name,
    address,
  };
};

const parseLocationPayloadText = (rawText) => {
  const trimmed = String(rawText || "").trim();
  if (!trimmed.startsWith("{")) {
    return null;
  }

  try {
    const decoded = JSON.parse(trimmed);
    if (decoded?.location && typeof decoded.location === "object") {
      return buildLocationFromData(decoded.location, trimmed);
    }

    return buildLocationFromData(decoded, trimmed);
  } catch {
    return null;
  }
};

const resolveMessageNameAddress = (message = {}) => ({
  name: String(message.location_name || message.name || message.loc_name || "").trim(),
  address: String(message.location_address || message.address || message.loc_address || "").trim(),
});

export const parseLocationMessage = (message = {}) => {
  const storedText = String(message.stored_message || "").trim();
  const displayText = String(message.text || "").trim();
  const parseSources = [storedText, displayText].filter(Boolean);
  const { name, address } = resolveMessageNameAddress(message);
  const directLat = coerceCoordinate(message.latitude ?? message.lat);
  const directLng = coerceCoordinate(message.longitude ?? message.lng);

  if (directLat !== null && directLng !== null) {
    return {
      latitude: directLat,
      longitude: directLng,
      label: resolveLocationLabel(name, address, displayText || storedText),
      name,
      address,
    };
  }

  for (const rawText of parseSources) {
    const parsedPayload = parseLocationPayloadText(rawText);
    if (parsedPayload?.latitude != null && parsedPayload?.longitude != null) {
      return {
        ...parsedPayload,
        name: name || parsedPayload.name,
        address: address || parsedPayload.address,
        label: resolveLocationLabel(
          name || parsedPayload.name,
          address || parsedPayload.address,
          displayText || rawText
        ),
      };
    }
  }

  for (const rawText of parseSources) {
    const urlCoords = parseCoordsFromGoogleMapsUrl(rawText);
    if (urlCoords) {
      return {
        ...urlCoords,
        label: resolveLocationLabel(name, address, displayText || rawText),
        name,
        address,
      };
    }
  }

  for (const rawText of parseSources) {
    if (isCoordinateString(rawText)) {
      const coordinateMatch = rawText.match(
        /^(-?\d{1,3}(?:\.\d+)?)\s*,\s*(-?\d{1,3}(?:\.\d+)?)$/
      );
      return {
        latitude: Number(coordinateMatch[1]),
        longitude: Number(coordinateMatch[2]),
        label: resolveLocationLabel(name, address, displayText || rawText),
        name,
        address,
      };
    }
  }

  for (const rawText of parseSources) {
    const parsedPayload = parseLocationPayloadText(rawText);
    if (parsedPayload) {
      return {
        ...parsedPayload,
        name: name || parsedPayload.name,
        address: address || parsedPayload.address,
        label: resolveLocationLabel(
          name || parsedPayload.name,
          address || parsedPayload.address,
          displayText || rawText
        ),
      };
    }
  }

  const fallbackText = displayText || storedText;
  if (!fallbackText) {
    const label = resolveLocationLabel(name, address, "");
    if (label !== DEFAULT_LOCATION_LABEL || name || address) {
      return {
        latitude: directLat,
        longitude: directLng,
        label,
        name,
        address,
      };
    }
    return null;
  }

  return {
    latitude: directLat,
    longitude: directLng,
    label: resolveLocationLabel(name, address, fallbackText),
    name,
    address,
  };
};

const hasExplicitLocationType = (message = {}) =>
  String(message?.msgType || message?.type || "").toLowerCase() === "location";

const hasLocationMetadata = (message = {}) =>
  Boolean(
    message.location_name
    || message.location_address
    || message.loc_name
    || message.loc_address
  );

const looksLikeLocationContent = (message = {}) => {
  const sources = [message.stored_message, message.text].filter(Boolean);

  for (const raw of sources) {
    const trimmed = String(raw).trim();
    if (!trimmed) {
      continue;
    }

    if (isCoordinateString(trimmed) || isGoogleMapsUrl(trimmed)) {
      return true;
    }

    if (trimmed.startsWith("{") && parseLocationPayloadText(trimmed)) {
      return true;
    }
  }

  return false;
};

export const isLocationMessage = (message = {}) => {
  if (hasExplicitLocationType(message)) {
    return true;
  }

  const parsed = parseLocationMessage(message);
  if (
    coerceCoordinate(parsed?.latitude) !== null
    && coerceCoordinate(parsed?.longitude) !== null
  ) {
    return true;
  }

  if (hasLocationMetadata(message)) {
    return true;
  }

  return looksLikeLocationContent(message);
};

export const getGoogleMapsUrl = (location) => {
  if (!location) {
    return null;
  }

  const latitude = coerceCoordinate(location.latitude);
  const longitude = coerceCoordinate(location.longitude);

  if (latitude !== null && longitude !== null) {
    return `https://www.google.com/maps/search/?api=1&query=${latitude},${longitude}`;
  }

  const urlCoords = parseCoordsFromGoogleMapsUrl(location.label);
  if (urlCoords?.latitude != null && urlCoords?.longitude != null) {
    return `https://www.google.com/maps/search/?api=1&query=${urlCoords.latitude},${urlCoords.longitude}`;
  }

  const searchableLabel = [
    location.label,
    [location.name, location.address].filter(Boolean).join(" - ") || null,
  ].find((value) => value && value !== DEFAULT_LOCATION_LABEL);

  if (searchableLabel) {
    return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(searchableLabel)}`;
  }

  return null;
};

export const canOpenLocationInGoogleMaps = (message) =>
  Boolean(getGoogleMapsUrl(parseLocationMessage(message)));

export const openLocationInGoogleMaps = (message) => {
  const location = parseLocationMessage(message);
  const url = getGoogleMapsUrl(location);

  if (!url) {
    return false;
  }

  window.open(url, "_blank", "noopener,noreferrer");
  return true;
};
